/**
 * <header-search-typewriter>
 * - Types out the placeholder text.
 * - Shows live results under the field as the shopper types, using Shopify's
 *   predictive search endpoint (/search/suggest.json). Enter still goes to the
 *   full search results page.
 * - Keeps the results list inside the visible screen area, including above the
 *   on-screen keyboard on phones, so every result can be scrolled to.
 */
class HeaderSearchTypewriter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    if (!this.input) return;

    this.setupTypewriter();
    this.setupPredictive();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    clearTimeout(this.debounceTimer);
    cancelAnimationFrame(this.fitFrame);
    if (this.abortController) this.abortController.abort();
    if (!this.input) return;
    if (this.handleFocus) this.input.removeEventListener('focus', this.handleFocus);
    if (this.onInput) {
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('keydown', this.onKeydown);
      this.input.removeEventListener('focus', this.onFocus);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('resize', this.onViewportChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.onViewportChange);
        window.visualViewport.removeEventListener('scroll', this.onViewportChange);
      }
      if (this.results) this.results.removeEventListener('touchmove', this.onResultsTouch);
    }
  }

  /* ---------------- Typewriter placeholder ---------------- */

  setupTypewriter() {
    this.fullText = this.dataset.placeholder || this.input.placeholder || '';
    this.speed = Number(this.dataset.speed) || 55;
    this.startDelay = Number(this.dataset.delay) || 400;

    // Respect reduced-motion and pre-filled search terms: show the text outright.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || this.input.value) {
      this.input.placeholder = this.fullText;
      return;
    }

    this.index = 0;
    this.input.placeholder = '';

    this.handleFocus = () => this.finish();
    this.input.addEventListener('focus', this.handleFocus, { once: true });

    this.timer = setTimeout(() => this.tick(), this.startDelay);
  }

  tick() {
    this.index += 1;
    this.input.placeholder = this.fullText.slice(0, this.index);

    if (this.index < this.fullText.length) {
      this.timer = setTimeout(() => this.tick(), this.speed);
    }
  }

  finish() {
    clearTimeout(this.timer);
    this.input.placeholder = this.fullText;
  }

  /* ---------------- Live results ---------------- */

  setupPredictive() {
    this.results = this.querySelector('.header-search__results');
    this.status = this.querySelector('[data-search-status]');
    if (!this.results || this.dataset.predictive === 'false') return;

    this.minChars = Number(this.dataset.minChars) || 2;
    this.debounceMs = 250;
    this.cache = new Map();
    this.activeIndex = -1;

    this.onInput = () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.search(), this.debounceMs);
    };
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onFocus = () => {
      if (this.input.value.trim().length >= this.minChars && this.results.childElementCount) this.open();
    };
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    // Re-fit the list when the visible area changes (keyboard opening/closing,
    // rotation, browser toolbars showing or hiding).
    this.onViewportChange = () => {
      if (!this.results.hidden) this.scheduleFit();
    };

    // Scrolling the results on a phone dismisses the keyboard so the whole
    // list has room, the same way most shop search boxes behave.
    this.onResultsTouch = () => {
      if (document.activeElement === this.input) this.input.blur();
    };

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.input.addEventListener('focus', this.onFocus);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('resize', this.onViewportChange);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewportChange);
      window.visualViewport.addEventListener('scroll', this.onViewportChange);
    }
    this.results.addEventListener('touchmove', this.onResultsTouch, { passive: true });
  }

  get root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  async search() {
    const term = this.input.value.trim();

    if (term.length < this.minChars) {
      if (this.abortController) this.abortController.abort();
      this.results.replaceChildren();
      this.close();
      return;
    }

    if (this.cache.has(term)) {
      this.render(term, this.cache.get(term));
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.setAttribute('loading', '');

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'query,product,collection,page',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag',
    });

    try {
      const response = await fetch(`${this.root}search/suggest.json?${params.toString()}`, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const data = await response.json();
      const results = (data && data.resources && data.resources.results) || {};
      this.cache.set(term, results);
      if (this.input.value.trim() === term) this.render(term, results);
    } catch (error) {
      if (error.name !== 'AbortError' && this.input.value.trim() === term) this.renderMessage(term, true);
    } finally {
      this.removeAttribute('loading');
    }
  }

  render(term, results) {
    const queries = (results.queries || []).slice(0, 4);
    const products = (results.products || []).slice(0, 6);
    const collections = (results.collections || []).slice(0, 3);
    const pages = (results.pages || []).slice(0, 3);
    const total = queries.length + products.length + collections.length + pages.length;

    if (!total) {
      this.renderMessage(term, false);
      return;
    }

    const fragment = document.createDocumentFragment();
    this.optionCount = 0;

    if (queries.length) {
      fragment.append(this.group('Suggestions', queries.map((q) => this.option(q.url, q.text))));
    }
    if (products.length) {
      fragment.append(this.group('Products', products.map((p) => this.productOption(p))));
    }
    if (collections.length) {
      fragment.append(this.group('Collections', collections.map((c) => this.option(c.url, c.title))));
    }
    if (pages.length) {
      fragment.append(this.group('Pages', pages.map((p) => this.option(p.url, p.title))));
    }

    fragment.append(this.viewAll(term));
    this.results.replaceChildren(fragment);
    this.results.scrollTop = 0;
    this.activeIndex = -1;
    this.announce(`${products.length} products found. Use the up and down arrows to browse.`);
    this.open();
  }

  renderMessage(term, isError) {
    const message = document.createElement('p');
    message.className = 'header-search__message';
    message.textContent = isError
      ? 'Search is unavailable right now. Press Enter to see all results.'
      : `No matches for \u201C${term}\u201D.`;
    this.optionCount = 0;
    this.results.replaceChildren(message, this.viewAll(term));
    this.activeIndex = -1;
    this.announce(message.textContent);
    this.open();
  }

  group(title, items) {
    const wrapper = document.createElement('div');
    wrapper.className = 'header-search__group';
    wrapper.setAttribute('role', 'group');
    wrapper.setAttribute('aria-label', title);
    const heading = document.createElement('p');
    heading.className = 'header-search__group-title';
    heading.setAttribute('aria-hidden', 'true');
    heading.textContent = title;
    wrapper.append(heading, ...items);
    return wrapper;
  }

  baseOption(url) {
    const link = document.createElement('a');
    link.className = 'header-search__option';
    link.href = url;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    link.setAttribute('tabindex', '-1');
    link.id = `${this.results.id}-option-${this.optionCount}`;
    this.optionCount += 1;
    return link;
  }

  option(url, text) {
    const link = this.baseOption(url);
    const label = document.createElement('span');
    label.className = 'header-search__title';
    label.textContent = text;
    link.append(label);
    return link;
  }

  productOption(product) {
    const link = this.baseOption(product.url);
    link.classList.add('header-search__option--product');

    const imageUrl = (product.featured_image && product.featured_image.url) || product.image;
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'header-search__thumb';
      image.src = this.sizedImage(imageUrl, 120);
      image.alt = '';
      image.width = 48;
      image.height = 48;
      image.loading = 'lazy';
      link.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'header-search__thumb';
      link.append(placeholder);
    }

    const meta = document.createElement('span');
    meta.className = 'header-search__meta';

    const title = document.createElement('span');
    title.className = 'header-search__title';
    title.textContent = product.title;
    meta.append(title);

    const price = this.formatPrice(product.price);
    if (price) {
      const priceLine = document.createElement('span');
      priceLine.className = 'header-search__price';
      priceLine.textContent = price;

      const compare = Number(product.compare_at_price_max);
      if (compare && compare > Number(product.price)) {
        const compareEl = document.createElement('s');
        compareEl.className = 'header-search__compare';
        compareEl.textContent = this.formatPrice(compare);
        priceLine.append(' ', compareEl);
      }
      meta.append(priceLine);
    }

    link.append(meta);
    return link;
  }

  viewAll(term) {
    const params = new URLSearchParams({ q: term, 'options[prefix]': 'last' });
    const link = this.baseOption(`${this.root}search?${params.toString()}`);
    link.classList.add('header-search__view-all');
    link.textContent = `See all results for \u201C${term}\u201D`;
    return link;
  }

  sizedImage(url, width) {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  formatPrice(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  get options() {
    return Array.from(this.results.querySelectorAll('.header-search__option'));
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      if (!this.results.hidden) {
        event.preventDefault();
        this.close();
      }
      return;
    }

    if (this.results.hidden) return;
    const options = this.options;
    if (!options.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.setActive((this.activeIndex + step + options.length) % options.length, options);
    } else if (event.key === 'Enter' && this.activeIndex >= 0 && options[this.activeIndex]) {
      event.preventDefault();
      window.location.href = options[this.activeIndex].href;
    }
  }

  setActive(index, options = this.options) {
    options.forEach((option, i) => {
      const active = i === index;
      option.classList.toggle('is-active', active);
      option.setAttribute('aria-selected', String(active));
    });
    this.activeIndex = index;
    const current = options[index];
    if (current) {
      this.input.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    }
  }

  /* ---------------- Sizing ---------------- */

  scheduleFit() {
    cancelAnimationFrame(this.fitFrame);
    this.fitFrame = requestAnimationFrame(() => this.fitToViewport());
  }

  // Limit the list's height to the part of the screen the shopper can actually
  // see. On phones the keyboard covers the page without shrinking it, so the
  // visual viewport is used rather than the page height.
  fitToViewport() {
    if (this.results.hidden) return;
    const viewport = window.visualViewport;
    const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const top = this.results.getBoundingClientRect().top;
    const available = Math.floor(visibleBottom - top - 12);
    const height = Math.max(160, Math.min(available, 560));
    this.results.style.maxHeight = `${height}px`;
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    this.fitToViewport();
    // The keyboard can still be animating in; measure again once it settles.
    this.scheduleFit();
    clearTimeout(this.refitTimer);
    this.refitTimer = setTimeout(() => this.fitToViewport(), 350);
  }

  close() {
    if (!this.results) return;
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.classList.remove('is-open');
    this.activeIndex = -1;
  }

  announce(text) {
    if (this.status) this.status.textContent = text;
  }
}

if (!customElements.get('header-search-typewriter')) {
  customElements.define('header-search-typewriter', HeaderSearchTypewriter);
}
